import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  getDocumentTypes,
  getReceiptTypes,
  getPaymentTypes,
  getVatRate,
  getSettings,
  getCustomerDebtReport,
} from '../../api/client';
import { useRivhitData } from '../../context/RivhitDataContext';
import {
  WizardStep,
  WizardData,
  DocCategory,
  DocType,
  ReceiptType,
  PaymentTypeItem,
  SelectedCustomer,
  SelectedItem,
  PaymentLine,
  CreatedDocument,
  AssistantDocumentPrefill,
  InvoiceSourceMode,
  initialWizardData,
  DOC_CATEGORIES,
} from './types';
import DocTypeSelector from './DocTypeSelector';
import CustomerStep from './CustomerStep';
import ItemsStep from './ItemsStep';
import PaymentsStep from './PaymentsStep';
import InvoiceSelectionStep from './InvoiceSelectionStep';
import DeliveryNoteSelectionStep from './DeliveryNoteSelectionStep';
import ReviewStep from './ReviewStep';
import SuccessStep from './SuccessStep';
import OpenBalanceWhatsAppModal from '../../components/OpenBalanceWhatsAppModal';

const STEPS_ORDER: WizardStep[] = [
  'select-type',
  'select-customer',
  'select-items',
  'payments',
  'select-invoices',
  'review',
  'success',
];

const STEP_LABELS: Record<WizardStep, string> = {
  'select-type': 'createDocument.steps.selectType',
  'select-customer': 'createDocument.steps.selectCustomer',
  'select-items': 'createDocument.steps.selectItems',
  'select-invoices': 'createDocument.steps.selectInvoices',
  payments: 'createDocument.steps.payments',
  review: 'createDocument.steps.review',
  success: 'createDocument.steps.success',
};

interface InvoiceDebtBlock {
  checking: boolean;
  amount: number;
  unpaid: number;
  days: number;
  error: string | null;
}

export default function CreateDocument() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const {
    configured,
    customers,
    customersLoading,
    ensureCustomersLoaded,
    items: rivhitItems,
    itemsLoading: rivhitItemsLoading,
    ensureItemsLoaded,
  } = useRivhitData();
  const [step, setStep] = useState<WizardStep>('select-type');
  const [data, setData] = useState<WizardData>({ ...initialWizardData });

  // Reference data
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [receiptTypes, setReceiptTypes] = useState<ReceiptType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeItem[]>([]);
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [refDataLoading, setRefDataLoading] = useState(true);
  const isRtl = i18n.dir() === 'rtl';
  const appliedPrefillKeyRef = useRef<string | null>(null);

  const [invoiceBlockDebtAgeDays, setInvoiceBlockDebtAgeDays] = useState(90);
  const [invoiceProvider, setInvoiceProvider] = useState<'rivhit' | 'heshbonit_beclick'>('rivhit');
  const [invoiceDebtBlock, setInvoiceDebtBlock] = useState<InvoiceDebtBlock>({
    checking: false,
    amount: 0,
    unpaid: 0,
    days: 90,
    error: null,
  });
  const [showInvoiceDebtWhatsAppModal, setShowInvoiceDebtWhatsAppModal] = useState(false);

  const resolveCategoryDefaults = useCallback((
    category: DocCategory,
    availableDocTypes: DocType[],
    availableReceiptTypes: ReceiptType[],
  ) => {
    let docType: number | null = null;
    let receiptType: number | null = null;
    let priceIncludeVat = false;

    if (category === 'receipt') {
      const defaultReceipt = availableReceiptTypes[0];
      receiptType = defaultReceipt?.receipt_type ?? null;
    } else if (category === 'invoice_receipt') {
      const filtered = availableDocTypes.filter((dt) => dt.is_invoice_receipt);
      const chosen = filtered[0] || availableDocTypes[0];
      docType = chosen?.document_type ?? null;
      priceIncludeVat = false;
    } else {
      const filtered = availableDocTypes.filter((dt) => {
        if (category === 'invoice') {
          return dt.is_accounting && !dt.document_name.toLowerCase().includes('delivery') && !dt.is_invoice_receipt;
        }
        if (category === 'delivery_note') {
          return dt.document_name.toLowerCase().includes('delivery') || dt.document_name.includes('תעודת משלוח');
        }
        return false;
      });
      const chosen = filtered[0] || availableDocTypes[0];
      docType = chosen?.document_type ?? null;
      priceIncludeVat = false;
    }

    return {
      docType,
      receiptType,
      priceIncludeVat,
    };
  }, []);

  const loadRefData = useCallback(async () => {
    try {
      const [dtRes, rtRes, ptRes, vatRes] = await Promise.all([
        getDocumentTypes(),
        getReceiptTypes(),
        getPaymentTypes(),
        getVatRate().catch(() => ({ data: null })),
      ]);
      setDocTypes(dtRes.data ?? []);
      setReceiptTypes(
        (rtRes.data ?? []).filter((rt: ReceiptType) => !rt.is_invoice_receipt),
      );
      if (vatRes.data?.vat_rate != null) {
        setVatRate(vatRes.data.vat_rate);
      }
      // Keep only: cash, check (incoming), credit card (primary), bank transfer
      const EXCLUDED_PAYMENT_KEYWORDS = [
        'other', 'אחר',
        'outgoing', 'יוצא',
      ];
      const ALLOWED_PAYMENT_KEYWORDS = [
        'cash', 'מזומן',
        'check', 'שיק', "צ'ק", 'צק',
        'credit', 'אשראי',
        'bank', 'העברה',
      ];
      setPaymentTypes(
        (ptRes.data ?? []).filter((pt: PaymentTypeItem) => {
          const name = pt.payment_name.toLowerCase();
          const isAllowed = ALLOWED_PAYMENT_KEYWORDS.some((kw) => name.includes(kw));
          const isExcluded = EXCLUDED_PAYMENT_KEYWORDS.some((kw) => name.includes(kw));
          return isAllowed && !isExcluded;
        }),
      );
    } catch {
      // non-critical — forms still work with empty ref data
    } finally {
      setRefDataLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRefData();
  }, [loadRefData]);

  useEffect(() => {
    let active = true;
    getSettings()
    .then((res) => {
      if (!active) return;
      const settings = res.data?.settings ?? res.data ?? {};
      const days = Number(settings.collections?.invoice_block_debt_age_days ?? settings.invoice_block_debt_age_days ?? 90);
      setInvoiceBlockDebtAgeDays(Number.isFinite(days) && days > 0 ? days : 90);
      setInvoiceProvider(settings.invoice_provider === 'heshbonit_beclick' ? 'heshbonit_beclick' : 'rivhit');
    })      .catch(() => {
        // Keep the default 90-day block threshold if settings are unavailable.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
    ensureItemsLoaded();
  }, [configured, ensureCustomersLoaded, ensureItemsLoaded]);

  useEffect(() => {
    if (!data.category) return;

    const nextDefaults = resolveCategoryDefaults(data.category, docTypes, receiptTypes);
    if (data.category === 'receipt') {
      if (data.receiptType || !nextDefaults.receiptType) return;
    } else {
      if (data.docType || !nextDefaults.docType) return;
    }

    setData((prev) => ({
      ...prev,
      ...nextDefaults,
    }));
  }, [data.category, data.docType, data.receiptType, docTypes, receiptTypes, resolveCategoryDefaults]);

  useEffect(() => {
    const assistantPrefill = (location.state as { assistantPrefill?: AssistantDocumentPrefill } | null)?.assistantPrefill;
    if (!assistantPrefill || refDataLoading) return;

    const prefillKey = JSON.stringify(assistantPrefill);
    if (appliedPrefillKeyRef.current === prefillKey) return;
    appliedPrefillKeyRef.current = prefillKey;

    const category = assistantPrefill.category ?? 'invoice';
    const defaults = resolveCategoryDefaults(category, docTypes, receiptTypes);
    const selectedCustomer = assistantPrefill.customer
      ? {
          customer_id: assistantPrefill.customer.customer_id ?? null,
          display_name: assistantPrefill.customer.display_name,
          is_free_text: assistantPrefill.customer.is_free_text ?? assistantPrefill.customer.customer_id == null,
        }
      : null;
    const selectedItems: SelectedItem[] = (assistantPrefill.items ?? []).map((item) => ({
      id: crypto.randomUUID(),
      item_id: item.item_id,
      catalog_number: item.catalog_number,
      description: item.description,
      quantity: item.quantity,
      price: item.price ?? 0,
      unit: item.unit || '',
      is_custom: item.is_custom ?? item.item_id == null,
    }));

    setData((prev) => ({
      ...prev,
      category,
      docType: defaults.docType,
      receiptType: category === 'receipt' ? defaults.receiptType : null,
      priceIncludeVat: defaults.priceIncludeVat,
      invoiceSourceMode: 'items',
      customer: selectedCustomer,
      items: selectedItems,
      deliveryNotes: [],
      comments: assistantPrefill.comments ?? '',
      payments: (category === 'receipt' || category === 'invoice_receipt') ? prev.payments : [],
      createdDoc: null,
    }));

    if (!selectedCustomer) {
      setStep('select-customer');
    } else if ((assistantPrefill.items ?? []).length === 0 && category !== 'receipt') {
      setStep('select-items');
    } else {
      setStep('review');
    }
  }, [location.state, refDataLoading, resolveCategoryDefaults, docTypes, receiptTypes]);

  // Derive visible steps per category
  const visibleSteps = STEPS_ORDER.filter((s) => {
    const cat = data.category;
    if (s === 'select-invoices') return cat === 'receipt';
    if (s === 'payments') return cat === 'receipt' || cat === 'invoice_receipt';
    if (s === 'select-items') return cat !== 'receipt';
    return true;
  });

  const currentIndex = visibleSteps.indexOf(step);
  const isInvoiceCategory = data.category === 'invoice' || data.category === 'invoice_receipt';
  const isInvoiceCreationBlocked = isInvoiceCategory && invoiceDebtBlock.amount > 0;
  const selectedCustomerPhone = data.customer?.customer_id
    ? customers.find((customer) => customer.customer_id === data.customer?.customer_id)?.phone
    : null;

  useEffect(() => {
    const customerId = data.customer?.customer_id;
    if (!isInvoiceCategory || !customerId) {
      setInvoiceDebtBlock({
        checking: false,
        amount: 0,
        unpaid: 0,
        days: invoiceBlockDebtAgeDays,
        error: null,
      });
      return;
    }

    let active = true;
    setInvoiceDebtBlock((prev) => ({
      ...prev,
      checking: true,
      amount: 0,
      unpaid: 0,
      days: invoiceBlockDebtAgeDays,
      error: null,
    }));

    const DEBT_CHECK_TIMEOUT_MS = 10_000;
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!active || settled) return;
      settled = true;
      setInvoiceDebtBlock({
        checking: false,
        amount: 0,
        unpaid: 0,
        days: invoiceBlockDebtAgeDays,
        error: t('createDocument.customerDebtCheckError', 'Could not verify customer debt. Try again before creating an invoice.'),
      });
    }, DEBT_CHECK_TIMEOUT_MS);

    getCustomerDebtReport(customerId, { debt_age_threshold_days: String(invoiceBlockDebtAgeDays) })
      .then((res) => {
        if (!active || settled) return;
        settled = true;
        clearTimeout(timeoutId);
        const amount = Number(res.data?.aged_debt_amount || 0);
        setInvoiceDebtBlock({
          checking: false,
          amount,
          unpaid: Number(res.data?.unpaid_amount || 0),
          days: Number(res.data?.payment_terms_days || invoiceBlockDebtAgeDays),
          error: null,
        });
        if (amount > 0 && step !== 'select-type' && step !== 'select-customer') {
          setStep('select-customer');
        }
      })
      .catch(() => {
        if (!active || settled) return;
        settled = true;
        clearTimeout(timeoutId);
        setInvoiceDebtBlock({
          checking: false,
          amount: 0,
          unpaid: 0,
          days: invoiceBlockDebtAgeDays,
          error: t('createDocument.customerDebtCheckError', 'Could not verify customer debt. Try again before creating an invoice.'),
        });
      });

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [data.customer?.customer_id, invoiceBlockDebtAgeDays, isInvoiceCategory, step, t]);

  const canGoNext = (): boolean => {
    switch (step) {
      case 'select-type':
        return data.category !== null && (data.docType !== null || data.receiptType !== null);
      case 'select-customer':
        return data.customer !== null
          && data.customer.display_name.trim().length > 0
          && !invoiceDebtBlock.checking
          && !isInvoiceCreationBlocked;
        // invoiceDebtBlock.error (API unavailable) shows a warning but does not block — user can proceed
      case 'select-items':
        return data.items.length > 0
          && data.items.every((i) => i.quantity > 0 && i.price >= 0);
      case 'payments':
        return data.payments.length > 0 && data.payments.every((p) => p.amount > 0);
      case 'select-invoices':
        return true; // Invoice selection is optional — user can skip
      default:
        return false;
    }
  };

  const goNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]);
    }
  };

  const goBack = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]);
    }
  };

  const resetWizard = () => {
    setData({ ...initialWizardData });
    setInvoiceDebtBlock({
      checking: false,
      amount: 0,
      unpaid: 0,
      days: invoiceBlockDebtAgeDays,
      error: null,
    });
    setShowInvoiceDebtWhatsAppModal(false);
    setStep('select-type');
  };

  // Map category selection to default Rivhit doc/receipt types
  const handleCategorySelect = (category: DocCategory) => {
    const nextDefaults = resolveCategoryDefaults(category, docTypes, receiptTypes);
    const typesReady = category === 'receipt'
      ? nextDefaults.receiptType !== null
      : nextDefaults.docType !== null;

    setData((prev) => ({
      ...prev,
      category,
      invoiceSourceMode: 'items',
      deliveryNotes: [],
      ...(typesReady ? nextDefaults : {}),
    }));

    // Auto-advance to next step on doc type selection
    setStep('select-customer');
  };

  const handleCustomerSelect = (customer: SelectedCustomer) => {
    setData((prev) => ({ ...prev, customer }));
  };

  const handleItemsChange = (items: SelectedItem[]) => {
    setData((prev) => ({ ...prev, items }));
  };

  const handleInvoiceSourceModeChange = (invoiceSourceMode: InvoiceSourceMode) => {
    setData((prev) => ({
      ...prev,
      invoiceSourceMode,
      items: invoiceSourceMode === 'items' ? prev.items : [],
      deliveryNotes: invoiceSourceMode === 'delivery_notes' ? prev.deliveryNotes : [],
    }));
  };

  const handleDeliveryNotesChange = (
    deliveryNotes: import('./types').DeliveryNoteSelection[],
    items: SelectedItem[],
  ) => {
    setData((prev) => ({ ...prev, deliveryNotes, items }));
  };

  const handlePaymentsChange = (payments: PaymentLine[]) => {
    setData((prev) => ({ ...prev, payments }));
  };

  const handleInvoiceAllocationsChange = (invoiceAllocations: import('./types').InvoiceAllocation[]) => {
    setData((prev) => ({ ...prev, invoiceAllocations }));
  };

  const handleCommentsChange = (comments: string) => {
    setData((prev) => ({ ...prev, comments }));
  };

  const handleSuccess = async (doc: CreatedDocument, allocResult?: import('./types').ReceiptWithAllocationsResult) => {
    setData((prev) => ({ ...prev, createdDoc: doc, allocationsResult: allocResult ?? null }));
    setStep('success');
  };

  const itemsTotal = data.items.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0,
  );
  // For invoice_receipt, payment must match the document total (including VAT if prices exclude VAT)
  const invoiceReceiptPaymentTotal = data.priceIncludeVat
    ? itemsTotal
    : itemsTotal + (vatRate != null ? itemsTotal * (vatRate / 100) : 0);
  // Progress indicator steps (exclude success)
  const progressSteps = visibleSteps.filter((s) => s !== 'success');

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div
        className={`grid grid-cols-1 gap-8 items-stretch ${isRtl ? 'lg:[direction:ltr]' : ''}`}
      >

        {/* Right Content: Wizard */}
        <div dir={isRtl ? 'rtl' : 'ltr'}>
          {/* Header */}
          <h1 className="text-2xl font-bold mb-1">
            {data.category
              ? t(DOC_CATEGORIES.find((c) => c.id === data.category)?.titleKey ?? 'createDocument.title', 'Create Document')
              : t('createDocument.title', 'Create Document')}
          </h1>
          <p className="text-gray-500 mb-6 text-sm">
            {t('createDocument.subtitle', 'Quickly create invoices, receipts, and delivery notes')}
          </p>

          {/* Progress bar */}
          {step !== 'success' && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                {progressSteps.map((s, i) => {
                  const sIdx = visibleSteps.indexOf(s);
                  const isActive = sIdx === currentIndex;
                  const isDone = sIdx < currentIndex;
                  const isClickable = isDone || isActive;
                  return (
                    <div key={s} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          disabled={!isClickable}
                          onClick={() => isClickable && setStep(s)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : isDone
                                ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                                : 'bg-gray-200 text-gray-500 cursor-default'
                          }`}
                        >
                          {isDone ? '✓' : i + 1}
                        </button>
                        <span
                          className={`text-xs mt-1 hidden sm:block ${
                            isActive ? 'text-blue-600 font-medium' : isDone ? 'text-green-600 cursor-pointer' : 'text-gray-400'
                          }`}
                          onClick={() => isClickable && setStep(s)}
                        >
                          {t(STEP_LABELS[s], s)}
                        </span>
                      </div>
                      {i < progressSteps.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 mx-2 ${
                            sIdx < currentIndex ? 'bg-green-500' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step content */}
          <div className="min-h-[400px]">
            {step === 'select-type' && (
              <DocTypeSelector
                selected={data.category}
                onSelect={handleCategorySelect}
              />
            )}

            {step === 'select-customer' && (
              <div className="space-y-4">
                <CustomerStep
                  selected={data.customer}
                  onSelect={handleCustomerSelect}
                  customers={customers}
                  loading={customersLoading}
                />

                {data.customer?.customer_id && isInvoiceCategory && invoiceDebtBlock.checking && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {t('createDocument.debtBlockChecking', 'Checking customer debt before invoice creation...')}
                  </div>
                )}

                {data.customer?.customer_id && isInvoiceCategory && invoiceDebtBlock.error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {invoiceDebtBlock.error}
                  </div>
                )}

                {data.customer?.customer_id && isInvoiceCreationBlocked && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-red-900">
                          {t('createDocument.debtBlockTitle', 'Invoice creation blocked')}
                        </p>
                        <p className="mt-1 text-sm text-red-800">
                          {t(
                            'createDocument.debtBlockMessage',
                            '{{name}} has ₪{{amount}} in open debt older than {{days}} days. An invoice or order cannot be created until the debt is paid.',
                            {
                              name: data.customer.display_name,
                              amount: invoiceDebtBlock.amount.toLocaleString(undefined, { maximumFractionDigits: 0 }),
                              days: invoiceDebtBlock.days,
                            },
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowInvoiceDebtWhatsAppModal(true)}
                        className="shrink-0 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                      >
                        {t('createDocument.success.sendDebtWhatsApp', 'Send debt notice via WhatsApp')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'select-items' && (
              <div className="space-y-5">
                {data.category === 'invoice' && invoiceProvider === 'rivhit' && (
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
                    <button
                      type="button"
                      onClick={() => handleInvoiceSourceModeChange('items')}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        data.invoiceSourceMode === 'items'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {t('createDocument.invoiceSource.items')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInvoiceSourceModeChange('delivery_notes')}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        data.invoiceSourceMode === 'delivery_notes'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {t('createDocument.invoiceSource.deliveryNotes')}
                    </button>
                  </div>
                )}

                {data.category === 'invoice' && data.invoiceSourceMode === 'delivery_notes' && invoiceProvider === 'rivhit' ? (
                  <DeliveryNoteSelectionStep
                    customerId={data.customer?.customer_id ?? null}
                    selectedNotes={data.deliveryNotes}
                    onChange={handleDeliveryNotesChange}
                  />
                ) : (
                  <ItemsStep
                    items={data.items}
                    onChange={handleItemsChange}
                    rivhitItems={rivhitItems}
                    rivhitItemsLoading={rivhitItemsLoading}
                    customer={data.customer}
                  />
                )}
              </div>
            )}

            {step === 'payments' && (
              <PaymentsStep
                payments={data.payments}
                onChange={handlePaymentsChange}
                paymentTypes={paymentTypes}
                totalAmount={
                  data.category === 'receipt'
                    ? 0
                    : data.category === 'invoice_receipt'
                      ? invoiceReceiptPaymentTotal
                      : itemsTotal
                }
                freeAmount={data.category === 'receipt'}
              />
            )}

            {step === 'select-invoices' && (
              <InvoiceSelectionStep
                customerId={data.customer?.customer_id ?? null}
                allocations={data.invoiceAllocations}
                onChange={handleInvoiceAllocationsChange}
                paidAmount={data.payments.reduce((s, p) => s + (p.amount || 0), 0)}
              />
            )}

            {step === 'review' && (
              <ReviewStep
                data={data}
                onCommentsChange={handleCommentsChange}
                onAllocationsChange={handleInvoiceAllocationsChange}
                onSuccess={handleSuccess}
              />
            )}

            {step === 'success' && data.createdDoc && (
              <SuccessStep
                document={data.createdDoc}
                allocationsResult={data.allocationsResult}
                customerId={data.customer?.customer_id}
                customerName={data.customer?.display_name}
                customerMessagesEnabled={data.customer ? customers.find((c) => c.customer_id === data.customer!.customer_id)?.allow_message === true : false}
                onReset={resetWizard}
              />
            )}
          </div>

          {/* Navigation buttons */}
          {step !== 'success' && (
            <div className="flex justify-between mt-8 pt-4 border-t">
              <button
                onClick={goBack}
                disabled={currentIndex === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  currentIndex === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {isRtl ? '→' : '←'} {t('common.back', 'Back')}
              </button>

              {step !== 'review' && (
                <button
                  onClick={goNext}
                  disabled={!canGoNext()}
                  className={`px-8 py-3 rounded-xl font-medium transition-colors ${
                    canGoNext()
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t('createDocument.next', 'Next')} {isRtl ? '←' : '→'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showInvoiceDebtWhatsAppModal && data.customer?.customer_id && (
        <OpenBalanceWhatsAppModal
          customerId={data.customer.customer_id}
          displayName={data.customer.display_name}
          phone={selectedCustomerPhone}
          fallbackOpenAmount={invoiceDebtBlock.unpaid}
          fallbackAgedAmount={invoiceDebtBlock.amount}
          fallbackAgeDays={invoiceDebtBlock.days}
          includeInvoiceBlockNotice
          messagesEnabled={customers.find((c) => c.customer_id === data.customer!.customer_id)?.allow_message === true}
          onClose={() => setShowInvoiceDebtWhatsAppModal(false)}
        />
      )}
    </div>
  );
}
